import { type CSSProperties } from "react";

export type OrzioMarkSize = "xs" | "sm" | "md" | "lg" | "xl" | "small" | "medium" | "large" | "hero" | "loading" | "appIcon";
export type OrzioMarkVariant = "light" | "dark" | "solid";

interface OrzioMarkProps {
  size?: OrzioMarkSize | number;
  animated?: boolean;
  animate?: boolean;
  glow?: boolean;
  className?: string;
  variant?: OrzioMarkVariant;
  title?: string;
}

const SIZE_PX: Record<Exclude<OrzioMarkSize, number>, number> = {
  xs: 24,
  sm: 32,
  md: 48,
  lg: 64,
  xl: 96,
  small: 32,
  medium: 48,
  large: 72,
  hero: 280,
  loading: 76,
  appIcon: 96,
};

const uid = () => Math.random().toString(36).slice(2);

export default function OrzioMark({
  size = "md",
  animated,
  animate,
  glow = true,
  className = "",
  variant = "light",
  title = "Orzio",
}: OrzioMarkProps) {
  const px = typeof size === "number" ? size : SIZE_PX[size];
  const shouldAnimate = animated ?? animate ?? true;
  const id = uid();
  const isAppIcon = size === "appIcon";
  const style = { "--orzio-mark-size": `${px}px` } as CSSProperties;

  return (
    <span
      className={[
        "orzio-mark",
        `orzio-mark--${variant}`,
        shouldAnimate ? "orzio-mark--animated" : "",
        glow ? "orzio-mark--glow" : "",
        isAppIcon ? "orzio-mark--app-icon" : "",
        className,
      ].filter(Boolean).join(" ")}
      style={style}
      role="img"
      aria-label={title}
    >
      <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <linearGradient id={`orzio-ring-${id}`} x1="22" y1="98" x2="98" y2="18" gradientUnits="userSpaceOnUse">
            <stop stopColor="#1447E6" />
            <stop offset="0.45" stopColor="#0B7CFF" />
            <stop offset="0.78" stopColor="#22D3EE" />
            <stop offset="1" stopColor="#B7F6FF" />
          </linearGradient>
          <linearGradient id={`orzio-deep-${id}`} x1="25" y1="92" x2="94" y2="25" gradientUnits="userSpaceOnUse">
            <stop stopColor="#1237B9" />
            <stop offset="0.58" stopColor="#0B62E8" />
            <stop offset="1" stopColor="#21C6F3" />
          </linearGradient>
          <radialGradient id={`orzio-core-${id}`} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(46 38) rotate(49) scale(76)">
            <stop stopColor="#F7FCFF" stopOpacity="0.95" />
            <stop offset="0.27" stopColor="#8FDFFF" stopOpacity="0.34" />
            <stop offset="0.63" stopColor="#102E7A" stopOpacity="0.38" />
            <stop offset="1" stopColor="#06122B" stopOpacity="0.82" />
          </radialGradient>
          <filter id={`orzio-glow-${id}`} x="-55%" y="-55%" width="210%" height="210%">
            <feGaussianBlur stdDeviation="4.5" result="blur" />
            <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0.04 0 0 0 0 0.49 0 0 0 0 1 0 0 0 0.85 0" result="blueBlur" />
            <feMerge>
              <feMergeNode in="blueBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={`orzio-soft-${id}`} x="-70%" y="-70%" width="240%" height="240%">
            <feGaussianBlur stdDeviation="8" />
          </filter>
        </defs>

        {isAppIcon && (
          <rect x="8" y="8" width="104" height="104" rx="24" fill="#F8FAFC" stroke="#D9E6F5" />
        )}

        <g className="orzio-mark__halo" filter={`url(#orzio-soft-${id})`}>
          <circle cx="60" cy="60" r="44" fill="#0B7CFF" opacity="0.18" />
          <circle cx="60" cy="60" r="28" fill="#22D3EE" opacity="0.1" />
        </g>

        <g className="orzio-mark__rings" filter={`url(#orzio-glow-${id})`}>
          <circle cx="60" cy="60" r="35.5" stroke="#D9E6F5" strokeWidth="13" opacity="0.62" />
          <path d="M60 24.5a35.5 35.5 0 0 1 31.7 19.6" stroke={`url(#orzio-ring-${id})`} strokeWidth="13" strokeLinecap="butt" />
          <path d="M95.1 65.4a35.5 35.5 0 0 1-15.3 24.3" stroke={`url(#orzio-deep-${id})`} strokeWidth="13" strokeLinecap="butt" />
          <path d="M62.5 95.4a35.5 35.5 0 0 1-33.9-23.1" stroke={`url(#orzio-ring-${id})`} strokeWidth="13" strokeLinecap="butt" />
          <path d="M25.7 50.9A35.5 35.5 0 0 1 48.9 26.3" stroke="#1237B9" strokeWidth="13" strokeLinecap="butt" />

          <path d="M84.3 35.5l8.8 8.6-8 8.3-8.9-8.9 8.1-8Z" fill="#22D3EE" />
          <path d="M88.7 61.6l12.4 5.1-4.9 12.1-12.7-5.1 5.2-12.1Z" fill="#0B7CFF" />
          <path d="M34.9 79.9l-8.4 8.7-9.1-8.8 8.4-8.8 9.1 8.9Z" fill="#1447E6" />
          <path d="M49.2 18.3h12.1v13.1H49.2V18.3Z" fill="#22D3EE" />
        </g>

        <g className="orzio-mark__inner">
          <circle cx="60" cy="60" r="20.8" fill={`url(#orzio-core-${id})`} />
          <circle cx="60" cy="60" r="23.8" stroke="#0B7CFF" strokeWidth="1.7" opacity="0.42" />
          <path d="M43.4 45.4a22.8 22.8 0 0 1 30.8-2.5" stroke="#DDFBFF" strokeWidth="1.4" strokeLinecap="round" opacity="0.42" />
          <ellipse cx="51" cy="45" rx="9" ry="4.7" fill="#FFFFFF" opacity="0.12" transform="rotate(-31 51 45)" />
        </g>

        <g className="orzio-mark__sweep">
          <path d="M88.2 27.8a43.5 43.5 0 0 1 14.4 25.4" stroke="#22D3EE" strokeWidth="2" strokeLinecap="round" opacity="0.62" />
          <path d="M21.5 76.4a43.5 43.5 0 0 1-2-24.4" stroke="#0B7CFF" strokeWidth="1.4" strokeLinecap="round" opacity="0.36" />
        </g>
      </svg>
    </span>
  );
}
